import { useEffect, useRef, useState } from 'react';

/**
 * Countdown of `seconds` that runs while `running` is true. Calls `onExpire` once at zero.
 * Returns the remaining seconds (or null when there is no time limit).
 */
export function useCountdown(seconds: number, running: boolean, resetKey: unknown, onExpire: () => void): number | null {
  const [remaining, setRemaining] = useState(seconds);
  const deadlineRef = useRef<number | null>(null);
  const expiredRef = useRef(false);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;

  useEffect(() => {
    setRemaining(seconds);
    deadlineRef.current = null;
    expiredRef.current = false;
  }, [seconds, resetKey]);

  useEffect(() => {
    if (seconds <= 0 || !running) return;
    if (deadlineRef.current === null) deadlineRef.current = Date.now() + seconds * 1000;
    const tick = () => {
      const left = Math.max(0, Math.ceil(((deadlineRef.current as number) - Date.now()) / 1000));
      setRemaining(left);
      if (left === 0 && !expiredRef.current) {
        expiredRef.current = true;
        onExpireRef.current();
      }
    };
    tick();
    const timer = window.setInterval(tick, 250);
    return () => window.clearInterval(timer);
  }, [seconds, running, resetKey]);

  return seconds > 0 ? remaining : null;
}
